import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { Tooltip } from '../../../../design-system/tooltip/tooltip';
import { cn } from '../../../../utils/cn';

type MaskedValueProps = {
  value?: string | null;
  maskedValue?: string;
  isRevealed?: boolean;
  isLoading?: boolean;
  onReveal?: () => void;
  onCopy?: () => void;
  className?: string;
  'aria-label'?: string;
};

export const MaskedValue = ({
  value,
  maskedValue,
  isRevealed,
  isLoading,
  onReveal,
  onCopy,
  className,
  'aria-label': ariaLabel,
}: MaskedValueProps) => {
  const [showCopied, setShowCopied] = useState(false);
  const displayValue = isRevealed ? value : maskedValue || value;
  const isClickableToReveal = !isRevealed && !!onReveal;
  const isClickableToCopy = isRevealed && !!onCopy && !!value;

  const handleCopy = () => {
    if (onCopy) {
      onCopy();
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 1500);
    }
  };

  if (isClickableToReveal) {
    return (
      <Tooltip
        trigger={
          <button
            type="button"
            onClick={onReveal}
            disabled={isLoading}
            className={cn(
              'cursor-pointer underline decoration-dotted underline-offset-2 transition-opacity hover:opacity-70',
              isLoading && 'animate-pulse cursor-wait',
              className,
            )}
            aria-label={ariaLabel ? `Reveal ${ariaLabel}` : 'Reveal value'}
          >
            {displayValue}
          </button>
        }
        content={isLoading ? 'Loading...' : 'Show details'}
        contentClassName="bg-foreground text-background px-2 py-1"
      />
    );
  }

  if (isClickableToCopy) {
    return (
      <Tooltip
        trigger={
          <button
            type="button"
            onClick={handleCopy}
            className={cn('cursor-pointer transition-opacity hover:opacity-70', className)}
            aria-label={ariaLabel ? `Copy ${ariaLabel}` : 'Copy value'}
          >
            {displayValue}
          </button>
        }
        content={
          showCopied ? (
            <span className="flex items-center gap-1">
              <Check className="h-3 w-3" /> Copied!
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <Copy className="h-3 w-3" /> Click to copy
            </span>
          )
        }
        contentClassName="bg-foreground text-background px-2 py-1"
        open={showCopied || undefined}
      />
    );
  }

  return <span className={className}>{displayValue}</span>;
};
